import { useState, useEffect } from 'react';
import { RATINGS, api, useToast, PanelHowTo } from './_barrel.js';


        function ProlificClustersPanel({ token, subject: initialSubject, reload, onSelectAccount }) {
            const [subject, setSubject] = useState(initialSubject);
            const refreshSubject = async () => {
                try {
                    const fresh = await api(`/api/v1/subjects/${initialSubject.id}`, {}, token);
                    setSubject(fresh);
                } catch (e) {
                    if (reload) reload();
                }
            };
            useEffect(() => { setSubject(initialSubject); }, [initialSubject]);
            const toast = useToast();
            const [busy, setBusy] = useState(null);
            const [snoozeFor, setSnoozeFor] = useState(null); // source_account or null
            const [showDormant, setShowDormant] = useState(false); // hide 0/0 accounts by default
            const [snoozeDays, setSnoozeDays] = useState(7);
            const [thresholdDraft, setThresholdDraft] = useState(subject.prolific_threshold_24h);
            const [editingThreshold, setEditingThreshold] = useState(false);
            const [isOpen, setIsOpen] = useState(false); // collapsed by default — the subject record opens compact

            const _rawClusters = subject.prolific_clusters || [];
            const _sevRank = { critical: 4, high: 3, moderate: 2, low: 1 };
            // Danger-first: hostile accounts sort above volume-only ones, by
            // severity then hostile count then recent volume. A hostile actor
            // is never hidden by a volume dismissal.
            const clusters = [..._rawClusters].sort((a, b) => {
                // Movement first: anything active in the last 7 days bubbles to
                // the top, so a quiet week reads as quiet and new activity is
                // obvious. Within that, danger-first (severity, hostile count).
                const aAct = (a.count_7d||0) > 0 ? 1 : 0;
                const bAct = (b.count_7d||0) > 0 ? 1 : 0;
                if (bAct !== aAct) return bAct - aAct;
                const sa = a.hostile_count ? (_sevRank[(a.max_hostile_severity||'').toLowerCase()] || 0) : 0;
                const sb = b.hostile_count ? (_sevRank[(b.max_hostile_severity||'').toLowerCase()] || 0) : 0;
                if (sb !== sa) return sb - sa;
                if ((b.hostile_count||0) !== (a.hostile_count||0)) return (b.hostile_count||0) - (a.hostile_count||0);
                return (b.count_7d||0) - (a.count_7d||0);
            });
            const prolific = clusters.filter(c => c.is_prolific);
            const hostileActors = clusters.filter(c => (c.hostile_count||0) > 0);
            const activeThisWeek = clusters.filter(c => (c.count_7d||0) > 0);
            const dismissed = clusters.filter(c => c.dismissal_kind);
            const press = subject.hostile_press;
            // Dormant = nothing recent to look at. An account counts as recent
            // if it posted in the 7d window OR had a hostile mention in the last
            // 30 days (the cluster counts use a strict 7d window, which is too
            // tight — hostile activity 8-30 days old is still worth showing).
            // Only truly stale accounts (no hostile activity for 30+ days) hide.
            const RECENT_MS = 30 * 24 * 60 * 60 * 1000;
            const isDormant = (c) => {
                if ((c.count_24h||0) > 0 || (c.count_7d||0) > 0) return false;
                const last = c.hostile_last_seen ? new Date(c.hostile_last_seen).getTime() : 0;
                return !last || (Date.now() - last) > RECENT_MS;
            };
            const dormantClusters = clusters.filter(isDormant);
            const visibleClusters = showDormant ? clusters : clusters.filter(c => !isDormant(c));

            async function doDismiss(account) {
                if (!confirm(`Permanently dismiss @${account}? Re-alerts only when activity hits 2x the current threshold.`)) return;
                setBusy(account);
                try {
                    await api(`/api/v1/subjects/${subject.id}/clusters/${encodeURIComponent(account)}/dismiss`,
                        { method: 'POST', body: JSON.stringify({}) }, token);
                    toast(`Dismissed @${account}`);
                    await refreshSubject();
                } catch (e) {
                    toast(`Dismiss failed: ${e.message}`, 'error');
                } finally {
                    setBusy(null);
                }
            }

            async function doSnooze(account) {
                const days = parseInt(snoozeDays, 10);
                if (!days || days < 1 || days > 365) { toast('Snooze must be 1-365 days', 'error'); return; }
                setBusy(account);
                try {
                    await api(`/api/v1/subjects/${subject.id}/clusters/${encodeURIComponent(account)}/snooze`,
                        { method: 'POST', body: JSON.stringify({ days }) }, token);
                    toast(`Snoozed @${account} for ${days} day${days === 1 ? '' : 's'}`);
                    setSnoozeFor(null);
                    await refreshSubject();
                } catch (e) {
                    toast(`Snooze failed: ${e.message}`, 'error');
                } finally {
                    setBusy(null);
                }
            }

            async function doClear(account) {
                setBusy(account);
                try {
                    await api(`/api/v1/subjects/${subject.id}/clusters/${encodeURIComponent(account)}/dismissal`,
                        { method: 'DELETE' }, token);
                    toast(`Re-enabled alerts for @${account}`);
                    await refreshSubject();
                } catch (e) {
                    toast(`Reactivate failed: ${e.message}`, 'error');
                } finally {
                    setBusy(null);
                }
            }

            async function saveThreshold() {
                const v = parseInt(thresholdDraft, 10);
                if (!v || v < 1 || v > 10000) { toast('Threshold must be 1-10000', 'error'); return; }
                try {
                    await api(`/api/v1/subjects/${subject.id}`,
                        { method: 'PATCH', body: JSON.stringify({ prolific_threshold_24h: v }) }, token);
                    toast(`Threshold set to ${v}`);
                    setEditingThreshold(false);
                    await refreshSubject();
                } catch (e) {
                    toast(`Save failed: ${e.message}`, 'error');
                }
            }

            const trendColour = (t) => t === 'rising' ? 'var(--red)' : t === 'fading' ? 'var(--text-muted)' : t === 'flat' ? 'var(--text-muted)' : 'var(--text)';

            return (
                <div style={{border:'1px solid var(--rule)', padding:'18px', marginBottom:'24px', background:'var(--surface)'}}>
                    <PanelHowTo id="prolific_clusters" />
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom: isOpen ? '14px' : '0', cursor:'pointer'}} onClick={() => setIsOpen(!isOpen)}>
                        <div>
                            <div className="font-mono text-xs uppercase tracking-widest text-muted">
                                <span style={{marginRight:'8px', display:'inline-block', width:'10px'}}>{isOpen ? '▾' : '▸'}</span>
                                Watched accounts
                            </div>
                            <div className="font-display text-2xl gold" style={{marginTop:'4px'}}>
                                {hostileActors.length > 0
                                    ? `${hostileActors.length} account${hostileActors.length===1?'':'s'} with hostile history`
                                    : (prolific.length === 0 ? 'No prolific accounts in window' : `${prolific.length} prolific in 24h`)}
                            </div>
                            <div className="text-xs text-muted" style={{marginTop:'2px'}}>
                                {activeThisWeek.length > 0
                                    ? <span style={{color:'var(--amber)', fontWeight:700}}>{activeThisWeek.length} active recently (30d)</span>
                                    : <span>none active in the last 30 days</span>}
                                {' · '}{clusters.length} tracked
                                {press && press.total > 0 && <span> · {press.last_7d > 0 ? `${press.last_7d} hostile press this week` : `${press.total} hostile press`}</span>}
                                {dismissed.length > 0 && ` · ${dismissed.length} suppressed`}
                            </div>
                        </div>
                        <div className="text-xs text-muted" style={{textAlign:'right'}} onClick={(e) => e.stopPropagation()}>
                            <div style={{marginBottom:'4px'}}>Threshold (24h)</div>
                            {editingThreshold ? (
                                <div style={{display:'flex', gap:'4px', alignItems:'center'}}>
                                    <input type="number" min="1" max="10000" value={thresholdDraft}
                                        onChange={e => setThresholdDraft(e.target.value)}
                                        style={{width:'60px', background:'var(--bg)', color:'var(--text)', border:'1px solid var(--rule)', padding:'2px 6px', fontSize:'12px'}}/>
                                    <button onClick={saveThreshold} style={{fontSize:'11px', padding:'2px 8px', background:'var(--gold)', color:'var(--bg)', border:'none', cursor:'pointer'}}>Save</button>
                                    <button onClick={() => { setEditingThreshold(false); setThresholdDraft(subject.prolific_threshold_24h); }} style={{fontSize:'11px', padding:'2px 8px', background:'transparent', color:'var(--text-muted)', border:'1px solid var(--rule)', cursor:'pointer'}}>Cancel</button>
                                </div>
                            ) : (
                                <div style={{display:'flex', gap:'8px', alignItems:'baseline'}}>
                                    <span className="font-display text-xl gold">{subject.prolific_threshold_24h}</span>
                                    <button onClick={() => setEditingThreshold(true)} style={{fontSize:'11px', color:'var(--text-muted)', background:'transparent', border:'none', cursor:'pointer', textDecoration:'underline'}}>edit</button>
                                </div>
                            )}
                        </div>
                    </div>

                    {isOpen && (<>
                    {press && press.total > 0 && (
                        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 12px', marginBottom:'10px', background:'rgba(201,169,110,0.08)', border:'1px solid rgba(201,169,110,0.25)', fontSize:'13px'}}>
                            <span style={{color:'var(--gold)'}}>📰 Press / News coverage <span style={{color:'var(--text-muted)', fontSize:'11px'}}>(no single account)</span></span>
                            <span style={{color:'var(--text-muted)'}}>
                                {press.total} hostile{press.last_7d > 0 && <span style={{color:'var(--amber)', fontWeight:700}}> · {press.last_7d} this week</span>}
                                {press.newest_at && ` · last ${new Date(press.newest_at).toLocaleDateString('en-GB', {day:'numeric', month:'short'})}`}
                            </span>
                        </div>
                    )}
                    {clusters.length === 0 ? (
                        <div className="text-sm text-muted" style={{padding:'12px 0'}}>
                            {press && press.total > 0
                                ? 'No social accounts to track — the hostile activity is press/news coverage (above), which has no single account.'
                                : 'No accounts have posted about this subject in the past 7 days. Clusters appear here when mentions arrive from Bluesky, Mastodon or X.'}
                        </div>
                    ) : visibleClusters.length === 0 ? (
                        <div className="text-sm text-muted" style={{padding:'12px 0'}}>
                            No accounts active in the last 30 days. {dormantClusters.length} dormant account{dormantClusters.length===1?'':'s'} with hostile history on file —{' '}
                            <button onClick={() => setShowDormant(true)} style={{color:'var(--gold)', background:'transparent', border:'none', cursor:'pointer', textDecoration:'underline', padding:0, font:'inherit'}}>show {dormantClusters.length}</button>.
                        </div>
                    ) : (
                        <table style={{width:'100%', fontSize:'13px', borderCollapse:'collapse'}}>
                            <thead>
                                <tr style={{textAlign:'left', color:'var(--text-muted)', fontSize:'11px', letterSpacing:'0.12em', textTransform:'uppercase'}}>
                                    <th style={{padding:'8px 8px 8px 0'}}>Account</th>
                                    <th style={{padding:'8px', textAlign:'right'}}>24h</th>
                                    <th style={{padding:'8px', textAlign:'right'}} title="Mentions from this account in the last 30 days">30d</th>
                                    <th style={{padding:'8px'}}>Trend</th>
                                    <th style={{padding:'8px'}}>Status</th>
                                    <th style={{padding:'8px', textAlign:'right'}}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleClusters.map(c => {
                                    const isDismissed = !!c.dismissal_kind;
                                    const isBusy = busy === c.source_account;
                                    const isSnoozing = snoozeFor === c.source_account;
                                    let statusText = '—';
                                    let statusColor = 'var(--text-muted)';
                                    let statusWeight = 400;
                                    // Hostile history is the dominant status. A volume
                                    // dismissal never hides a threat actor.
                                    if ((c.hostile_count||0) > 0) {
                                        const sev = (c.max_hostile_severity||'').toLowerCase();
                                        const hd = c.hostile_last_seen ? new Date(c.hostile_last_seen).toLocaleDateString('en-GB', {day:'numeric', month:'short'}) : '';
                                        statusText = `THREAT · ${sev.toUpperCase()} · ${c.hostile_count} hostile${hd ? ' · last '+hd : ''}`;
                                        statusColor = RATINGS.hex(sev);   // canonical: critical red, high amber, moderate gold
                                        statusWeight = 600;
                                    }
                                    else if (c.is_prolific) { statusText = 'PROLIFIC'; statusColor = 'var(--red)'; statusWeight = 600; }
                                    else if (c.dismissal_kind === 'permanent') { statusText = 'dismissed'; statusColor = 'var(--text-muted)'; }
                                    else if (c.dismissal_kind === 'snooze') {
                                        const until = c.snooze_until ? new Date(c.snooze_until) : null;
                                        statusText = until ? `snoozed until ${until.toLocaleDateString('en-GB')}` : 'snoozed';
                                        statusColor = 'var(--amber)';
                                    }
                                    const _hostile = (c.hostile_count||0) > 0;
                                    return (
                                        <tr key={c.source_account}
                                            onClick={_hostile ? () => onSelectAccount?.(c.source_account) : undefined}
                                            title={_hostile ? 'Show hostile posts' : undefined}
                                            style={{borderTop:'1px solid var(--rule)', opacity: isDismissed ? 0.6 : 1, cursor: _hostile ? 'pointer' : 'default'}}>
                                            <td style={{padding:'10px 8px 10px 0', color:'var(--text)'}}>
                                                {(c.count_7d||0) > 0 && <span title="Active in the last 7 days" style={{color:'var(--amber)', marginRight:'6px', fontWeight:700}}>●</span>}
                                                @{c.source_account}
                                            </td>
                                            <td style={{padding:'10px 8px', textAlign:'right', color: c.count_24h > 0 ? 'var(--text)' : 'var(--text-muted)'}}>{c.count_24h}</td>
                                            <td style={{padding:'10px 8px', textAlign:'right', color: (c.count_7d||0) > 0 ? 'var(--amber)' : 'var(--text-muted)', fontWeight: (c.count_7d||0) > 0 ? 700 : 400}}>{c.count_7d}</td>
                                            <td style={{padding:'10px 8px', color: trendColour(c.trend), fontSize:'12px'}}>{c.trend}</td>
                                            <td style={{padding:'10px 8px', color: statusColor, fontSize:'12px', fontWeight: statusWeight}}>{statusText}</td>
                                            <td style={{padding:'10px 0', textAlign:'right'}}>
                                                {isSnoozing ? (
                                                    <div style={{display:'inline-flex', gap:'4px', alignItems:'center'}}>
                                                        <input type="number" min="1" max="365" value={snoozeDays}
                                                            onChange={e => setSnoozeDays(e.target.value)}
                                                            style={{width:'50px', background:'var(--bg)', color:'var(--text)', border:'1px solid var(--rule)', padding:'2px 4px', fontSize:'11px'}}/>
                                                        <span style={{fontSize:'11px', color:'var(--text-muted)'}}>days</span>
                                                        <button disabled={isBusy} onClick={() => doSnooze(c.source_account)} style={{fontSize:'11px', padding:'3px 8px', background:'var(--gold)', color:'var(--bg)', border:'none', cursor: isBusy ? 'wait' : 'pointer'}}>OK</button>
                                                        <button onClick={() => setSnoozeFor(null)} style={{fontSize:'11px', padding:'3px 8px', background:'transparent', color:'var(--text-muted)', border:'1px solid var(--rule)', cursor:'pointer'}}>×</button>
                                                    </div>
                                                ) : isDismissed ? (
                                                    <button disabled={isBusy} onClick={() => doClear(c.source_account)}
                                                        style={{fontSize:'11px', padding:'4px 10px', background:'transparent', color:'var(--gold)', border:'1px solid var(--gold)', cursor: isBusy ? 'wait' : 'pointer'}}>
                                                        Reactivate
                                                    </button>
                                                ) : (
                                                    <div style={{display:'inline-flex', gap:'4px'}}>
                                                        <button disabled={isBusy} onClick={() => setSnoozeFor(c.source_account)}
                                                            style={{fontSize:'11px', padding:'4px 10px', background:'transparent', color:'var(--amber)', border:'1px solid var(--amber)', cursor: isBusy ? 'wait' : 'pointer'}}>
                                                            Snooze
                                                        </button>
                                                        <button disabled={isBusy} onClick={() => doDismiss(c.source_account)}
                                                            style={{fontSize:'11px', padding:'4px 10px', background:'transparent', color:'var(--red)', border:'1px solid var(--red)', cursor: isBusy ? 'wait' : 'pointer'}}>
                                                            Dismiss
                                                        </button>
                                                    </div>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                    {dormantClusters.length > 0 && visibleClusters.length > 0 && (
                        <div className="text-xs text-muted" style={{marginTop:'10px'}}>
                            {showDormant
                                ? <button onClick={() => setShowDormant(false)} style={{color:'var(--gold)', background:'transparent', border:'none', cursor:'pointer', textDecoration:'underline', padding:0, font:'inherit'}}>Hide {dormantClusters.length} dormant account{dormantClusters.length===1?'':'s'}</button>
                                : <button onClick={() => setShowDormant(true)} style={{color:'var(--gold)', background:'transparent', border:'none', cursor:'pointer', textDecoration:'underline', padding:0, font:'inherit'}}>Show {dormantClusters.length} dormant account{dormantClusters.length===1?'':'s'} with hostile history (nothing in 30 days)</button>}
                        </div>
                    )}
                    </>)}
                </div>
            );
        }

export { ProlificClustersPanel };
